import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Form } from "react-bootstrap";
import LoadingSpinner from "../../Components/Loading/LoadingSpinner";
import UniToast from "../../Components/UniToast/UniToast";
import StoreMoneyPanel from "./components/StoreMoneyPanel";
import {
  fetchStoreRequestDetail,
  approveStoreRequest,
  declineStoreRequest,
  creditStorePayment,
  clearStoreRequestDetail,
  reset,
} from "../../store/actions/MarketSlice";

const STATUS_COLORS = {
  pending: "#f5a623",
  approved: "#01b574",
  declined: "#ff1733",
};

const WALLET_COLORS = {
  ok: "#01b574",
  grace: "#f5a623",
  deactivated: "#ff1733",
};

function StatusBadge({ status }) {
  const color = STATUS_COLORS[status] || "#a0aec0";
  return (
    <span
      className="text-uppercase fw-bold"
      style={{
        color,
        border: `1px solid ${color}`,
        borderRadius: "12px",
        padding: "2px 10px",
        fontSize: "0.85rem",
      }}
    >
      {status}
    </span>
  );
}

function WalletBadge({ status }) {
  const color = WALLET_COLORS[status] || "#a0aec0";
  return (
    <span
      className="text-uppercase fw-bold"
      style={{
        color,
        border: `1px solid ${color}`,
        borderRadius: "12px",
        padding: "2px 10px",
        fontSize: "0.85rem",
      }}
    >
      {status || "—"}
    </span>
  );
}

function formatMoney(value) {
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

export default function StoreRequestDetail() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    storeRequestDetail,
    storeRequestDetailLoading,
    approveLoading,
    approveSuccess,
    declineLoading,
    declineSuccess,
    creditPaymentLoading,
    creditPaymentSuccess,
    error,
  } = useSelector((state) => state.market);

  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [showCreditForm, setShowCreditForm] = useState(false);
  const [creditAmount, setCreditAmount] = useState("");
  const [creditNote, setCreditNote] = useState("");
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);

  useEffect(() => {
    dispatch(fetchStoreRequestDetail(id));
    return () => {
      dispatch(clearStoreRequestDetail());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (approveSuccess) {
      setShowSuccessToast(true);
      const timeout = setTimeout(() => {
        dispatch(reset());
        navigate("/dashboard/market");
      }, 1500);
      return () => clearTimeout(timeout);
    }
  }, [approveSuccess, dispatch, navigate]);

  useEffect(() => {
    if (declineSuccess) {
      setShowSuccessToast(true);
      const timeout = setTimeout(() => {
        dispatch(reset());
        navigate("/dashboard/market");
      }, 1500);
      return () => clearTimeout(timeout);
    }
  }, [declineSuccess, dispatch, navigate]);

  useEffect(() => {
    // Deliberately doesn't navigate away — unlike approve/decline, crediting
    // a payment keeps the store on this page so the admin can see the
    // wallet status flip from grace/deactivated back to ok in place.
    if (creditPaymentSuccess) {
      setShowSuccessToast(true);
      setShowCreditForm(false);
      setCreditAmount("");
      setCreditNote("");
      const timeout = setTimeout(() => dispatch(reset()), 1500);
      return () => clearTimeout(timeout);
    }
  }, [creditPaymentSuccess, dispatch]);

  useEffect(() => {
    if (error) {
      setShowErrorToast(true);
    }
  }, [error]);

  const handleApprove = () => {
    dispatch(approveStoreRequest(id));
  };

  const handleConfirmDecline = () => {
    dispatch(declineStoreRequest({ id, reason: declineReason }));
  };

  const handleCreditPayment = () => {
    const amount = Number(creditAmount);
    if (!amount || amount <= 0) return;
    dispatch(creditStorePayment({ id, amount, note: creditNote }));
  };

  if (storeRequestDetailLoading) {
    return <LoadingSpinner />;
  }

  if (!storeRequestDetail) {
    return (
      <div className="text-center text-grey mt-5">
        <h5>No data yet</h5>
      </div>
    );
  }

  const store = storeRequestDetail;

  return (
    <div className="text-white" data-aos="fade-up" data-aos-duration="600">
      <div className="d-flex align-items-center gap-3 mb-4">
        {store?.logo_url && (
          <img
            src={store.logo_url}
            alt="logo"
            width={70}
            height={70}
            style={{ objectFit: "cover", borderRadius: "12px" }}
          />
        )}
        <div>
          <h3 className="fw-bold mb-1">{store?.name || "—"}</h3>
          <StatusBadge status={store?.approval_status} />
        </div>
      </div>

      <div className="card-performance rounded-20 p-4 mb-4">
        <p className="text-grey mb-1">Description</p>
        <p className="mb-3">{store?.description || "—"}</p>

        <p className="text-grey mb-1">Currency</p>
        <p className="mb-3">{store?.currency || "—"}</p>

        <p className="text-grey mb-1">Countries</p>
        <p className="mb-0">
          {store?.countries?.length > 0
            ? store.countries.map((c) => c.name).join(", ")
            : "—"}
        </p>
      </div>

      <div className="card-performance rounded-20 p-4 mb-4">
        <p className="text-white fs-5 fw-bold mb-3">Owner</p>
        <p className="mb-1">
          <span className="text-grey">Name: </span>
          {store?.owner?.name || "—"}
        </p>
        <p className="mb-1">
          <span className="text-grey">Email: </span>
          {store?.owner?.email || "—"}
        </p>
        <p className="mb-3">
          <span className="text-grey">Phone: </span>
          {store?.owner?.phone || "—"}
        </p>

        <p className="text-grey mb-2">National ID Card</p>
        <div className="d-flex gap-3 flex-wrap mb-3">
          {store?.owner?.front_card_image && (
            <img
              src={store.owner.front_card_image}
              alt="front card"
              width={300}
              style={{ borderRadius: "8px" }}
            />
          )}
          {store?.owner?.back_card_image && (
            <img
              src={store.owner.back_card_image}
              alt="back card"
              width={300}
              style={{ borderRadius: "8px" }}
            />
          )}
        </div>

        <p className="text-grey mb-2">Verification Selfie</p>
        {store?.verification_selfie_url ? (
          <img
            src={store.verification_selfie_url}
            alt="verification selfie"
            width={300}
            style={{ borderRadius: "8px" }}
          />
        ) : (
          <p className="text-grey">—</p>
        )}
      </div>

      {store?.approval_status === "approved" && (
        <div className="card-performance rounded-20 p-4 mb-4">
          <p className="text-white fs-5 fw-bold mb-3">Wallet</p>
          <div className="d-flex align-items-center gap-4 flex-wrap mb-3">
            <div>
              <p className="text-grey mb-1">Balance</p>
              <p className="mb-0 fs-5">
                {store?.currency || ""} {formatMoney(store?.wallet_balance)}
              </p>
            </div>
            <div>
              <p className="text-grey mb-1">Status</p>
              <WalletBadge status={store?.wallet_status} />
            </div>
            {store?.wallet_status === "grace" && store?.wallet_grace_deadline && (
              <div>
                <p className="text-grey mb-1">Auto-deactivates on</p>
                <p className="mb-0">
                  {new Date(store.wallet_grace_deadline * 1000).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>

          {(store?.wallet_status === "grace" || store?.wallet_status === "deactivated") &&
            (!showCreditForm ? (
              <Button
                onClick={() => setShowCreditForm(true)}
                className="border-0 text-uppercase text-white rounded bg-green"
              >
                Credit Payment
              </Button>
            ) : (
              <div>
                <p className="text-grey mb-2">Amount ({store?.currency || "—"})</p>
                <Form.Control
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(e.target.value)}
                  placeholder="e.g. 150.00"
                  className="mb-3"
                  style={{
                    maxWidth: 240,
                    backgroundColor: "#2d2d2d",
                    color: "#ecf0f1",
                    border: "1px solid #495057",
                  }}
                />
                <p className="text-grey mb-2">Note (optional)</p>
                <Form.Control
                  type="text"
                  value={creditNote}
                  onChange={(e) => setCreditNote(e.target.value)}
                  placeholder="e.g. Bank transfer ref #1234"
                  className="mb-3"
                  style={{
                    backgroundColor: "#2d2d2d",
                    color: "#ecf0f1",
                    border: "1px solid #495057",
                  }}
                />
                <div className="d-flex gap-3">
                  <Button
                    onClick={handleCreditPayment}
                    disabled={
                      creditPaymentLoading || !creditAmount || Number(creditAmount) <= 0
                    }
                    className="border-0 text-uppercase text-white rounded bg-green"
                  >
                    {creditPaymentLoading ? "Crediting..." : "Confirm Credit"}
                  </Button>
                  <Button
                    onClick={() => {
                      setShowCreditForm(false);
                      setCreditAmount("");
                      setCreditNote("");
                    }}
                    disabled={creditPaymentLoading}
                    className="border-0 text-uppercase text-white rounded bg-custom-gray"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ))}
        </div>
      )}

      {store?.approval_status === "pending" ? (
        <div className="card-performance rounded-20 p-4 mb-4">
          {!showDeclineForm ? (
            <div className="d-flex gap-3">
              <Button
                onClick={handleApprove}
                disabled={approveLoading || declineLoading}
                className="border-0 text-uppercase text-white rounded bg-green"
              >
                {approveLoading ? "Approving..." : "Approve"}
              </Button>
              <Button
                onClick={() => setShowDeclineForm(true)}
                disabled={approveLoading || declineLoading}
                className="border-0 text-uppercase text-white rounded bg-delete"
              >
                Decline
              </Button>
            </div>
          ) : (
            <div>
              <p className="text-grey mb-2">Decline reason (optional)</p>
              <textarea
                className="form-control mb-3"
                rows={4}
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="Provide a reason for declining this store application"
                style={{
                  backgroundColor: "#2d2d2d",
                  color: "#ecf0f1",
                  border: "1px solid #495057",
                }}
              />
              <div className="d-flex gap-3">
                <Button
                  onClick={handleConfirmDecline}
                  disabled={declineLoading}
                  className="border-0 text-uppercase text-white rounded bg-delete"
                >
                  {declineLoading ? "Declining..." : "Confirm Decline"}
                </Button>
                <Button
                  onClick={() => {
                    setShowDeclineForm(false);
                    setDeclineReason("");
                  }}
                  disabled={declineLoading}
                  className="border-0 text-uppercase text-white rounded bg-custom-gray"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="card-performance rounded-20 p-4 mb-4">
          <p className="mb-0">
            This application has already been{" "}
            <span className="text-capitalize">{store?.approval_status}</span>{" "}
            and is read-only.
          </p>
        </div>
      )}

      {store?.id && <StoreMoneyPanel storeId={store.id} refreshKey={creditPaymentSuccess} />}

      {showSuccessToast && (
        <UniToast
          open={showSuccessToast}
          setOpen={setShowSuccessToast}
          title="Success"
          message={
            approveSuccess
              ? "Store approved successfully."
              : creditPaymentSuccess
              ? "Payment credited to the store's wallet."
              : "Store application declined and removed."
          }
          reset={() => setShowSuccessToast(false)}
        />
      )}

      {showErrorToast && error && (
        <UniToast
          open={showErrorToast}
          setOpen={setShowErrorToast}
          title="Error"
          message={error}
          reset={() => {
            setShowErrorToast(false);
            dispatch(reset());
          }}
        />
      )}
    </div>
  );
}
